import { Box } from '@chakra-ui/react';
import { Code as Key } from 'constants/keyboard';
import { usePrevious } from 'hooks/usePrevious';
import {
  CustomOnDrag,
  CustomOnDragEnd,
  CustomOnDragStart,
  CustomOnResize,
  CustomOnResizeEnd,
  CustomOnRotateEnd,
} from 'modules/Editor/components/BoundingBox/BoundingBox.types';
import { useFlow } from 'modules/Editor/components/FlowCore';
import { ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { OnChangeEventTypes } from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { calculateNewRect } from 'widgets/Widget.helpers';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  TOP_LAYER_Z_INDEX,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';
import { InteractiveElementWrapper } from 'widgets/sdk/InteractiveElements';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { LINE_HANDLER_MAP } from './LineHandlers';
import { LINE_TYPE_MAP } from './LineRenders';
import { DEFAULT_DELTA_POS } from './LineWidget.config';
import {
  calculatePosByRatio,
  calculatePosListByAngle,
  calculateTargetPoint,
  calculateWidgetRect,
  checkAllConnectedPortsSelected,
  getEndPos,
  getHandleSize,
  getMidPosList,
  getPosByKeyEvent,
  getStartPos,
  getStepByKeyEvent,
  mergePosList,
} from './LineWidget.helpers';
import { multiEditConfig } from './LineWidget.multiEdit';
import {
  DeltaPos,
  LineProps,
  LineWidgetData,
  PortData,
  Pos,
  Rect,
  SetFocusProps,
  WidgetState,
} from './LineWidget.types';
import { LineWidgetControlKeyboardShortcuts } from './LineWidgetControlKeyboardShortcuts';
import { LineWidgetToolbarMenu } from './toolbar/LineWidgetToolbar';

const LineWidget = (): ReactElement => {
  const {
    leftPx,
    topPx,
    widthPx,
    heightPx,
    startPos,
    midPosList,
    endPos,
    type,
    strokeWidth,
    strokeColor,
    strokeDashType,
    startArrowStyle,
    endArrowStyle,
    rotateDeg,
    widgetId,
    startPort,
    endPort,
    isLocked,
    updateWidget,
    updateWidgetAndMoveBehind,
    isWidgetSelected,
  } = useWidget<LineWidgetData>();
  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();
  const lastStartPort = usePrevious(startPort);
  const lastEndPort = usePrevious(endPort);

  const [delta, setDelta] = useState<DeltaPos>(DEFAULT_DELTA_POS);
  const [activeHandlerIndex, setActiveHandlerIndex] = useState<number>(-1);
  const [widgetState, setWidgetState] = useState(WidgetState.Default);

  const dragHandlerRef = useRef<HTMLDivElement | null>(null);

  const {
    zoom,
    isGroupMember,
    setWidgetFocus,
    blur,
    enableEditorKeyboardShortcuts,
    disableEditorKeyboardShortcuts,
    widgetRefs,
  } = useEditor();

  const handlerSize = useMemo(() => getHandleSize(zoom), [zoom]);
  const Line = useMemo(() => LINE_TYPE_MAP[type] as React.ElementType, [type]);
  const LineHandler = useMemo(() => LINE_HANDLER_MAP[type] as React.ElementType, [type]);

  const { flowCoreEvent } = useFlow();

  const posListProps = useMemo(() => mergePosList(startPos, midPosList, endPos), [startPos, midPosList, endPos]);
  const [posListState, setPosListState] = useState(posListProps);
  const prevPosListProps = usePrevious(posListState);
  const isPosListStateUpdated = useMemo(() => prevPosListProps !== posListState, [prevPosListProps, posListState]);
  const posList = useMemo(() => {
    let result = isPosListStateUpdated ? posListState : posListProps;

    if (rotateDeg !== 0) {
      result = calculatePosListByAngle(widthPx, heightPx, result, rotateDeg);
    }

    return result;
  }, [rotateDeg, widthPx, heightPx, isPosListStateUpdated, posListProps, posListState]);

  // When dragging or resizing, these hold the real-time rect values of the start and end widgets
  const [realTimeStartRect, setRealTimeStartRect] = useState<Rect | null>(null);
  const [realTimeEndRect, setRealTimeEndRect] = useState<Rect | null>(null);
  // When dragging or resizing, these hold the real-time start/end ports of the line
  const [realTimeStartPort, setRealTimeStartPort] = useState<PortData | null>(null);
  const [realTimeEndPort, setRealTimeEndPort] = useState<PortData | null>(null);
  // Bool to determine if the real-time rect should be used or not.
  const [isUseRealTimeStartRect, setIsUseRealTimeStartRect] = useState<boolean>(false);
  const [isUseRealTimeEndRect, setIsUseRealTimeEndRect] = useState<boolean>(false);

  // Whether to use default moveable event or not for custom drag + resize handlers
  const checkShouldUseMoveableEvent = useCallback(
    (target: Element): boolean => {
      const isAllPortsSelected = checkAllConnectedPortsSelected(widgetId);

      // Disable moveable event if in flow mode and not all ports are selected
      if (target && (!!startPort?.widgetId || !!endPort?.widgetId) && !isAllPortsSelected) return false;

      return true;
    },
    [endPort?.widgetId, startPort?.widgetId, widgetId],
  );

  const calculateDelta = useCallback(
    (matrix: DOMMatrix, target: HTMLElement): DeltaPos => {
      const observingWidgetId = target.id;
      const isStartPort = startPort?.widgetId === observingWidgetId;
      const isEndPort = endPort?.widgetId === observingWidgetId;
      const targetPortData = isStartPort ? startPort : isEndPort ? endPort : null;

      const targetIndex = isStartPort ? 0 : isEndPort ? posList.length - 1 : null;
      if (targetIndex === null || targetPortData === null) return DEFAULT_DELTA_POS;

      const targetPoint = calculateTargetPoint(target, matrix, targetPortData);

      const linePoint = [leftPx + posList[targetIndex].xPx, topPx + posList[targetIndex].yPx];
      const customDelta = [targetPoint[0] - linePoint[0], targetPoint[1] - linePoint[1]];
      if (isStartPort || isEndPort) {
        const newRect = calculateNewRect(target, matrix);

        // update real time start / end port widget rect data
        if (isStartPort) {
          setRealTimeStartRect(newRect);
          setRealTimeStartPort(null);
          // todo: not sure why port is set to null here.
        } else if (isEndPort) {
          setRealTimeEndRect(newRect);
          setRealTimeEndPort(null);
        }
      }

      const result = {
        targetIndex,
        xPx: customDelta[0],
        yPx: customDelta[1],
      };
      return result;
    },
    [startPort, endPort, leftPx, topPx, posList],
  );

  // NOTE: Do not use 'delta'
  // Since the delta value is state so that it has the race condition issue with moveablajs events
  const generateLineWidgetData = useCallback(
    (matrix: DOMMatrix, target: HTMLElement) => {
      const calcDelta = calculateDelta(matrix, target);
      const result = calculateWidgetRect(leftPx, topPx, calcDelta, posList);
      setDelta(DEFAULT_DELTA_POS);

      // Reset real time rect data to null when done updating connected widgets via delta.
      setRealTimeEndRect(null);
      setRealTimeStartRect(null);
      setRealTimeStartPort(null);
      setRealTimeEndPort(null);

      return result;
    },
    [leftPx, topPx, posList, calculateDelta, setDelta],
  );

  // Subscribe this line widget events to the FlowCoreContext
  useEffect(() => {
    flowCoreEvent.onChange(widgetId, (type, matrix, target) => {
      if (type === OnChangeEventTypes.Update) {
        // Update: The event type that is called from the Mutation Callback when dragging the connected widget
        const calcDelta = calculateDelta(matrix, target);
        setDelta(calcDelta);

        // Update the widget style to match the new position and dimensions
        const result = calculateWidgetRect(leftPx, topPx, calcDelta, posList);
        const widget = widgetRefs?.[widgetId]?.element;
        if (widget) {
          widget.style.top = `${result.topPx}px`;
          widget.style.left = `${result.leftPx}px`;
          widget.style.width = `${result.widthPx}px`;
          widget.style.height = `${result.heightPx}px`;

          const lineSvgEl = widget.firstChild as HTMLElement;
          if (lineSvgEl) {
            lineSvgEl.style.transform = `translate(${leftPx - result.leftPx}px, ${topPx - result.topPx}px)`;
          }
        }
      } else if (type === OnChangeEventTypes.Done) {
        // Reset svg transform
        const widget = widgetRefs?.[widgetId]?.element;
        if (widget) {
          const lineSvgEl = widget.firstChild as HTMLElement;
          lineSvgEl.style.transform = '';
        }

        // Done: A callback function called from onDragEnd or onResizeEnd, which calculates and returns the current line widget data.
        return generateLineWidgetData(matrix, target);
      }
    });
  }, [widgetId, flowCoreEvent, calculateDelta, posList, widgetRefs, generateLineWidgetData, leftPx, topPx]);

  // Custom update data function for custom handlers of the line widget
  const handleUpdateData = useCallback(
    (data: LineWidgetData, targetWidgetId?: WidgetId) => {
      // If targetWidgetId is provided, move the widget behind the targetWidgetId
      // Currently used when attaching a line to the center port
      if (targetWidgetId) {
        updateWidgetAndMoveBehind(data, targetWidgetId);
      } else {
        updateWidget(data);
      }
      setDelta(DEFAULT_DELTA_POS);
    },
    [updateWidget, updateWidgetAndMoveBehind],
  );

  // Move custom handler through key event
  const handleKeyEvent = useCallback(
    (key: Key, isShift: boolean) => {
      const delta: DeltaPos = {
        targetIndex: activeHandlerIndex,
        ...getPosByKeyEvent(key, getStepByKeyEvent(isShift)),
      };
      const newWidgetData = calculateWidgetRect(leftPx, topPx, delta, posList);
      if (newWidgetData) {
        handleUpdateData(newWidgetData);
        setPosListState(mergePosList(newWidgetData.startPos, newWidgetData.midPosList, newWidgetData.endPos));
      }
    },
    [leftPx, topPx, posList, activeHandlerIndex, handleUpdateData, setPosListState],
  );

  // Custom onDragStart event for line widget, prevents dragging body of line if already connected
  const customOnDragStart = useCallback(
    ({ event, onDragStart }: CustomOnDragStart) => {
      const shouldUseMoveableEvent = checkShouldUseMoveableEvent(event.target as Element);

      // If dragging body of line (not end port), disallow drag if already connected
      if (!shouldUseMoveableEvent) return;

      // Call default drag start event
      onDragStart(event);
    },
    [checkShouldUseMoveableEvent],
  );

  // Custom onDrag event for line widget, prevents dragging body of line if already connected
  const customOnDrag = useCallback(
    ({ event, onDrag }: CustomOnDrag) => {
      const shouldUseMoveableEvent = checkShouldUseMoveableEvent(event.target as Element);

      // If dragging body of line (not end port), disallow drag if already connected
      if (!shouldUseMoveableEvent) return;

      // Call default drag event
      onDrag(event);

      if (dragHandlerRef.current) {
        dragHandlerRef.current.style.transform = event.target.style.transform;
        dragHandlerRef.current.style.zIndex = `${TOP_LAYER_Z_INDEX}`;
      }
    },
    [checkShouldUseMoveableEvent],
  );

  // Custom onDragEnd event for line widget, prevents dragging body of line if already connected
  const customOnDragEnd = useCallback(
    ({ event, onDragEnd }: CustomOnDragEnd) => {
      const shouldUseMoveableEvent = checkShouldUseMoveableEvent(event.target as Element);

      // If dragging body of line (not end port), disallow drag if already connected
      if (!shouldUseMoveableEvent) return;

      // Call default drag end event
      onDragEnd(event);

      if (dragHandlerRef.current) {
        dragHandlerRef.current.style.transform = '';
      }
    },
    [checkShouldUseMoveableEvent],
  );

  // Custom onResize handler to update the arrow line positions to allow for live rendering
  const customOnResize = useCallback(
    ({ event, onResize }: CustomOnResize) => {
      const shouldUseMoveableEvent = checkShouldUseMoveableEvent(event.target as Element);

      // If dragging body of line (not end port), disallow drag if already connected
      if (!shouldUseMoveableEvent) return;

      // Ignore keyboard shortcut
      if (!event?.inputEvent) return;

      const ratio = event.width / widthPx;
      const scaledStartPos: Pos = calculatePosByRatio(startPos, ratio);
      const scaledMidPosList: Pos[] = midPosList.map((pos) => calculatePosByRatio(pos, ratio));
      const scaledEndPos: Pos = calculatePosByRatio(endPos, ratio);
      const scaledPosList: Pos[] = mergePosList(scaledStartPos, scaledMidPosList, scaledEndPos);
      const rotatedPosList = calculatePosListByAngle(widthPx, heightPx, scaledPosList, -rotateDeg);

      setPosListState(rotatedPosList);

      onResize(event);
    },
    [checkShouldUseMoveableEvent, widthPx, startPos, midPosList, endPos, heightPx, rotateDeg],
  );

  // trigger onResizeEnd with resized pos data.
  const customOnResizeEnd = useCallback(
    ({ event, onResizeEnd }: CustomOnResizeEnd) => {
      const shouldUseMoveableEvent = checkShouldUseMoveableEvent(event.target as Element);

      // If dragging body of line (not end port), disallow drag if already connected
      if (!shouldUseMoveableEvent) return;

      // Ignore keyboard shortcut
      if (!event?.inputEvent) return;

      onResizeEnd(event, {
        startPos: getStartPos(posList),
        midPosList: getMidPosList(posList),
        endPos: getEndPos(posList),
      });
    },
    [checkShouldUseMoveableEvent, posList],
  );

  /**
   * Do not trigger onRotateEnd to prevent to save from the bounding box event
   */
  const customOnRotateEnd = useCallback(
    ({ event, onRotateEnd, isGroup }: CustomOnRotateEnd) => {
      // For the group rotate event, use the rotate event in the bounding box
      if (isGroup) {
        onRotateEnd(event);
        return;
      }

      const angle = event.lastEvent.rotate;
      const newPosList = calculatePosListByAngle(widthPx, heightPx, posList, angle);
      const newWidgetData = calculateWidgetRect(leftPx, topPx, delta, newPosList);
      if (newWidgetData) {
        newWidgetData.rotateDeg = 0;
        handleUpdateData(newWidgetData);

        // Reset bounding box changes
        event.target.style.transform = '';
      }
    },
    [posList, delta, heightPx, widthPx, leftPx, topPx, handleUpdateData],
  );

  const handleFocus = useCallback(
    ({ moveNext, movePrev, index, isWidget }: SetFocusProps) => {
      // Reset the activeElement
      blur();

      // Change focus to the widget
      if (isWidget) {
        setWidgetFocus();
        setActiveHandlerIndex(-1);
        if (widgetState !== WidgetState.Active) setWidgetState(WidgetState.Active);
        return;
      }

      // Change focus to the custom handler
      if (moveNext) {
        const nextIndex = posList.length <= activeHandlerIndex + 1 ? 0 : activeHandlerIndex + 1;
        setActiveHandlerIndex(nextIndex);
      } else if (movePrev) {
        const prevIndex = activeHandlerIndex - 1 < 0 ? posList.length - 1 : activeHandlerIndex - 1;
        setActiveHandlerIndex(prevIndex);
      } else if (typeof index === 'number') {
        setActiveHandlerIndex(index);
      }
      setWidgetState(WidgetState.Edit);
    },
    [widgetState, activeHandlerIndex, posList, setWidgetFocus, blur],
  );

  /**
   * Change the widgetState and activeElement(focus) according to the isWidgetSelected
   * Default - Rendered widget without focusing
   * Active - Selected widget with focusing
   * Edit - Selected the custom handler with focusing
   */
  useEffect(() => {
    // Check whether to use key event according to the widgetState
    if (widgetState === WidgetState.Default || widgetState === WidgetState.Active) enableEditorKeyboardShortcuts();
    if (isWidgetSelected && widgetState === WidgetState.Edit) {
      // duplicate and delete shortcuts still allowed
      disableEditorKeyboardShortcuts({
        allowedKeyboardKeys: [Key.D, Key.Backspace, Key.Delete],
      });
    }

    // Change the widgetState and initialize the handler index according to the current state
    if (!isWidgetSelected && widgetState !== WidgetState.Default) setWidgetState(WidgetState.Default);
    if (isWidgetSelected && widgetState === WidgetState.Default) {
      setWidgetState(WidgetState.Active);
      setActiveHandlerIndex(-1);
    }
  }, [isWidgetSelected, widgetState, enableEditorKeyboardShortcuts, disableEditorKeyboardShortcuts]);

  // if the widget is deleted ensure that the keyboard shortcuts are enabled again
  useEffect(() => () => enableEditorKeyboardShortcuts(), [enableEditorKeyboardShortcuts]);

  // Update line connection cache on mount/unmount
  useEffect(() => {
    // Start port changed
    if (startPort?.widgetId) {
      lineConnectionCache.attachLineToWidget(startPort.widgetId, widgetId);
    }

    // End port changed
    if (endPort?.widgetId) {
      lineConnectionCache.attachLineToWidget(endPort.widgetId, widgetId);
    }

    return () => {
      // Detach line from the line connection cache if widget is deleted or unmounted
      if (startPort?.widgetId) {
        lineConnectionCache.detachLineFromWidget(startPort.widgetId, widgetId);
      }
      if (endPort?.widgetId) {
        lineConnectionCache.detachLineFromWidget(endPort.widgetId, widgetId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the line connection cache if there is a connection change
  useEffect(() => {
    // Start port changed
    if (startPort?.widgetId !== lastStartPort?.widgetId) {
      if (startPort?.widgetId) {
        lineConnectionCache.attachLineToWidget(startPort.widgetId, widgetId);
      }

      if (lastStartPort?.widgetId) {
        lineConnectionCache.detachLineFromWidget(lastStartPort.widgetId, widgetId);
      }
    }

    // End port changed
    if (endPort?.widgetId !== lastEndPort?.widgetId) {
      if (endPort?.widgetId) {
        lineConnectionCache.attachLineToWidget(endPort.widgetId, widgetId);
      }

      if (lastEndPort?.widgetId) {
        lineConnectionCache.detachLineFromWidget(lastEndPort.widgetId, widgetId);
      }
    }
  }, [startPort, endPort, widgetId, lastStartPort, lastEndPort]);

  const lineProps: LineProps = {
    posList,
    id,
    widthPx,
    heightPx,
    delta,
    strokeWidth,
    strokeColor,
    strokeDashType,
    startArrowStyle,
    endArrowStyle,
    startPort,
    endPort,
    topPx,
    leftPx,
    realTimeStartRect,
    realTimeEndRect,
    realTimeStartPort,
    realTimeEndPort,
    isUseRealTimeStartRect,
    isUseRealTimeEndRect,
    fitSvgToPathSize: true,
  };

  return (
    <>
      <WidgetBase
        onResize={customOnResize}
        onResizeEnd={customOnResizeEnd}
        onRotateEnd={customOnRotateEnd}
        onDragStart={customOnDragStart}
        onDrag={customOnDrag}
        onDragEnd={customOnDragEnd}
        disableSingleSelect
        isIgnoredSmartGuideTarget={!!startPort?.widgetId || !!endPort?.widgetId}
      >
        {/* pointerEvents is disabled to use the custom selection area in the line renderer */}
        <Box
          w='100%'
          h='100%'
          position='relative'
          data-testid='line-widget'
          pointerEvents='none'
          style={{ transform: isPosListStateUpdated ? 'rotate(0deg)' : `rotate(${-rotateDeg}deg)` }}
        >
          <WidgetToolbar multiEditConfig={multiEditConfig}>
            <LineWidgetToolbarMenu />
          </WidgetToolbar>
          {isWidgetSelected && (
            <LineWidgetControlKeyboardShortcuts
              setFocus={handleFocus}
              setWidgetState={setWidgetState}
              widgetState={widgetState}
              onKeyEvent={handleKeyEvent}
            />
          )}

          <Line {...lineProps} />
        </Box>
      </WidgetBase>
      {/* Drag handler - should appear above other widgets */}
      {isWidgetSelected && !isGroupMember && !isLocked && (
        <Box
          position='absolute'
          z-index={TOP_LAYER_Z_INDEX}
          ref={dragHandlerRef}
          top={`${topPx}px`}
          left={`${leftPx}px`}
        >
          <LineHandler
            widgetId={widgetId}
            posList={posList}
            leftPx={leftPx}
            topPx={topPx}
            delta={delta}
            zoom={zoom}
            handlerSize={handlerSize}
            activeHandlerIndex={activeHandlerIndex}
            startPort={startPort}
            endPort={endPort}
            setFocus={handleFocus}
            setDelta={setDelta}
            updateData={handleUpdateData}
            setRealTimeStartRect={setRealTimeStartRect}
            setRealTimeEndRect={setRealTimeEndRect}
            setRealTimeStartPort={setRealTimeStartPort}
            setRealTimeEndPort={setRealTimeEndPort}
            setIsUseRealTimeStartRect={setIsUseRealTimeStartRect}
            setIsUseRealTimeEndRect={setIsUseRealTimeEndRect}
          />
        </Box>
      )}
    </>
  );
};

const ReadOnlyLineWidget = ({ includeAltTextImg }: WidgetBaseProp): ReactElement => {
  const {
    widthPx,
    heightPx,
    startPos,
    midPosList,
    endPos,
    type,
    strokeWidth,
    strokeColor,
    strokeDashType,
    startArrowStyle,
    endArrowStyle,
    startPort,
    endPort,
    topPx,
    leftPx,
    renderType,
    interactions,
  } = useWidget<LineWidgetData>();
  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();

  const posList = useMemo(() => mergePosList(startPos, midPosList, endPos), [startPos, midPosList, endPos]);
  const Line = LINE_TYPE_MAP[type] as React.ElementType;

  // When dragging or resizing, these hold the real-time rect values of the start and end widgets
  const [realTimeStartRect] = useState<Rect | null>(null);
  const [realTimeEndRect] = useState<Rect | null>(null);
  // When dragging or resizing, these hold the real-time start/end ports of the line
  const [realTimeStartPort] = useState<PortData | null>(null);
  const [realTimeEndPort] = useState<PortData | null>(null);
  // Bool to determine if the real-time rect should be used or not.
  const [isUseRealTimeStartRect] = useState<boolean>(false);
  const [isUseRealTimeEndRect] = useState<boolean>(false);

  const lineProps: LineProps = {
    posList,
    id,
    delta: DEFAULT_DELTA_POS,
    widthPx,
    heightPx,
    strokeWidth,
    strokeColor,
    strokeDashType,
    startArrowStyle,
    endArrowStyle,
    startPort,
    endPort,
    topPx,
    leftPx,
    realTimeStartRect,
    realTimeEndRect,
    realTimeStartPort,
    realTimeEndPort,
    isUseRealTimeStartRect,
    isUseRealTimeEndRect,
  };
  const isPreview = renderType === ReadOnlyRenderType.StructureTreeList;

  return (
    <ReadOnlyWidgetBase>
      <InteractiveElementWrapper interactions={isPreview ? interactions : undefined}>
        <Box w='100%' h='100%' position='relative' data-testid='line-widget'>
          {includeAltTextImg && <TransparentImg />}
          <Line {...lineProps} isReadonly />
        </Box>
      </InteractiveElementWrapper>
    </ReadOnlyWidgetBase>
  );
};

export { LineWidget, ReadOnlyLineWidget };
